import type { ElementType } from 'react';
import type { ISize, Line, Vector2d } from '@picsart/web-layering/types';
import type { IUserInfo } from '@picsart/miniapp-sdk-types/index';
import type { UserModel } from '@picsart/rc/types';
import type { ToolTypes } from 'configs/constants';
import type { IRemotePresetShelf } from 'services/presets/types';

import type { IUIState, ICanvasSize, IBatchData, IBatchState, IBatchImage, IAsyncJob, IUserBackground, SimpleTextType, ICropAttrsBatch, IBgToolRatio } from './store';
import type { ResponseStatus } from '../models';

// Please:
// Reuse general types,
// Try to avoid conditional keys,
// Do not use any "any" in this file,

declare global {
  interface Window {
    mozIndexedDB: IDBFactory;
    webkitIndexedDB: IDBFactory;
    msIndexedDB: IDBFactory;
    shimIndexedDB: IDBFactory;
  }
}

export type { IUIState, ICanvasSize, IBatchData, IBatchState, IBatchImage, IAsyncJob, IUserBackground, SimpleTextType, ICropAttrsBatch, IBgToolRatio };

export type MaskType = HTMLImageElement | HTMLCanvasElement;
export type UpscaleFactor = 2 | 4;

export enum EditorTypes {
  photo = 'photo',
  video = 'video',
}

export interface IRatioCard {
  id: string;
  name: string;
  title?: string;
  size: ISize;
}

export interface ICardsData extends IRatioCard {
  ratio_type: EditorTypes;
  icon: ElementType;
}

export type SubscriptionTierType = 'max_starter' | 'pro' | 'plus' | 'gold_old' | 'free' | 'max_performance' | 'power';

export interface TierFeaturesMap {
  id: SubscriptionTierType;
  level: number;
  storage_limit_in_mb: number;
  features: { id: string; name: string }[];
  current_country_specifications?: {
    country_code?: string;
    free_trial_credits?: number;
  };
}

export interface IUserData {
  isAuthorized: boolean;
  isSubscribed: boolean;
  isTrialUsed: boolean;
  waitingUserSubscription: boolean;
  subscriptionType: string | null;
  userLocation?: string;
  tiers: TierFeaturesMap[];
}

export interface IGeneralState {
  user: IUserData;
  isDarkModeEnabled: boolean;
  useOsTheme: boolean;
}

export interface IReduxState {
  general: IGeneralState;
  ui: IUIState;
  batch: IBatchState;
}

export interface ITheme {
  variables: Record<string, any>;
  scrollbars: Record<string, any>;
  colors: Record<string, any>;
  getColor: (value: string) => string;
  spacings: Record<string, string>;
  radiuses: Record<string, string>;
}

export interface IUser {
  key: string;
  id: number;
  name: string;
  username: string;
  photo: string;
  subscription: {
    granted: boolean;
    is_trial_used: boolean;
  };
  email?: string;
  account_type?: string;
  created?: string;
  // needed for monetization
  subscribed: boolean;
  isTrialUsed: boolean;
  roles?: string[];
}

export interface IORHistory {
  lineImage?: MaskType | undefined;
  line?: Line[];
  resultUrl?: string;
  toolType?: ToolTypes;
  isReset?: boolean;
  appliedMaskImg?: string;
}

export type UpscaleQueryParams = {
  photo_id: string;
  upscale_factor: string;
};

export interface IORHistoryApply {
  lineImage?: MaskType | undefined;
  line?: Line[];
  resultUrl?: string;
  isReset?: boolean;
  appliedMaskImg?: string;
  toolType?: ToolTypes;
}

export type TAnyObject = { [key: string]: any };

export interface ICropPatch extends Vector2d, ISize {}

export interface IToasterInfo {
  error: { [key: string]: { title: string; text: string } };
}

export interface ICdnUploadResponse {
  status: string;
  result: {
    download_url: string;
    url: string;
  };
}

export interface ITemplateSizes {
  id: string;
  name: string; // need this field to be synced with backend ratio_card
  title: string;
  className: string;
  iconName: string;
  type: number;
  size: ISize;
  preview_url?: string;
  show_as_category?: boolean;
}

export interface GeneralError {
  message?: string;
  errorMessage?: string;
}

export type User = Omit<IUserInfo & UserModel, 'id'> & {
  id?: string | number;
};

export interface IFetchResponse<T> {
  status: 'success' | 'error';
  message?: string;
  reason?: string;
  response: T;
  metadata?: { next_page?: string };
}

export type IItem = {
  icon?: string;
  prompt: string;
  id: string;
  title?: string;
};

export type IImageBackgroundItem = {
  id: string;
  title: string;
  icon: string;
  imageUrl: string;
};

export type Prompts = {
  categories: Array<{
    title: string;
    items: string[];
  }>;
  items: IItem[];
  title: string;
};

export type ToolEntry = {
  id: string;
  title?: string;
};

export type ToolsConfig = {
  desktop?: ToolEntry[];
  mobile?: ToolEntry[];
};

export type ImageBackgrounds = {
  categories: Array<{
    title: string;
    items: IImageBackgroundItem[];
  }>;
};

export type RemoteSettings = {
  batch_editor_miniapp: {
    prompts?: Prompts;
    imageBackgrounds?: ImageBackgrounds;
    preset_shelves?: IRemotePresetShelf[];
    tools?: ToolsConfig;
    [key: string]: any;
  };
};

export type IAIBGRequestResult = {
  status: ResponseStatus;
};

export type ImageLayer = {
  getParams(): {
    settings: {
      photo: string;
    };
  };
};

export type VideoLayer = {
  getParams(): {
    settings: {
      video: string;
    };
  };
};
